/**
 * Chooses which Garaimon appears, from what the player gathered.
 *
 * The rule is one sentence: a creature's chance is how well the gathered colours
 * match it, scaled by how rare it is, and opened up by the any-colour pixels the
 * player carries. Three details make it behave:
 *
 * - **Nothing is granted.** There is no exploration floor. A creature whose
 *   colours were not gathered scores zero on the match and therefore has no
 *   chance, so the panel reads zero across the board before the seed is launched.
 *   The old floor made every creature's chance at least a fifth the moment the
 *   continent was chosen, which is the opposite of what the colours are supposed
 *   to mean.
 * - **Rarity is the world's, not the player's.** Each tier carries a weight and a
 *   ceiling. The weight is how the world is stocked, so it decides the odds when
 *   the player has gathered nothing to prefer. The ceiling is a hard cap, so a
 *   legendary can never become likely however well the player aims - without it,
 *   a pool of a handful of creatures would make a legendary the *easiest* thing
 *   on the board to force.
 * - **An any-colour pixel keeps the field open.** It is a pixel of every colour at
 *   once, so it adds a little mass everywhere and stops the odds collapsing onto
 *   one creature. The seed the player launches always counts as one, which is the
 *   structural guarantee that a reveal is never reached with none of them.
 *
 * A true tie is settled by species id rather than by rarity: rarity has already
 * had its say in the weight, and a tie means the game has no colour reason to
 * prefer either creature.
 */

import { ANCHOR_COUNT, matchScore, type ColourMixture } from './colour';
import { COLLECT_CONFIG, rarityRung } from './collectConfig';
import { profileFor, profileMixturesOn } from './colourProfiles';
import { getCreature } from './roster';
import type { ContinentId, RarityTier } from './types';

export interface EncounterCandidate {
  readonly speciesId: string;
  /** Which rung of the ladder this creature sits on, for the reel and the readout. */
  readonly rarity: RarityTier;
  /** How close the collection is to this creature's colours, 0 to 1. */
  readonly matchScore: number;
  /** This creature's share of the selection weight, 0 to 1. Probabilities sum to 1. */
  readonly probability: number;
}

export interface EncounterSelection {
  readonly chosenSpeciesId: string;
  /** Every candidate on the continent, best first. */
  readonly candidates: readonly EncounterCandidate[];
}

/**
 * Orders candidates best first, resolving ties by species id.
 *
 * A tie in weight means the game has no reason to prefer either creature, so the
 * rule has to be arbitrary but stable and explainable. The species id is both,
 * and unlike rarity it says nothing about the creature's value - rarity has
 * already been applied to the weight itself.
 */
export function sortCandidates<T extends { matchScore: number; speciesId: string }>(candidates: T[]): T[] {
  return candidates.sort((a, b) => (b.matchScore === a.matchScore ? (a.speciesId < b.speciesId ? -1 : 1) : b.matchScore - a.matchScore));
}

/**
 * The gathered colours with the any-colour pixels folded in.
 *
 * An any-colour pixel is every colour at once, so it contributes an equal share
 * to every anchor rather than to one. That is what makes it keep the whole field
 * open: adding it lifts whatever the player did not gather without changing the
 * ratios they did gather. Returns the mixture unchanged when there are none, so
 * the ordinary path allocates nothing.
 */
export function augmentMixture(mixture: ColourMixture, wildcards: number): ColourMixture {
  if (!(wildcards > 0)) return mixture;
  const mass = new Array<number>(ANCHOR_COUNT).fill(0);
  for (let index = 0; index < ANCHOR_COUNT; index += 1) mass[index] = (mixture.mass[index] ?? 0) + wildcards / ANCHOR_COUNT;
  return { mass, total: mixture.total + wildcards };
}

/**
 * Shares that respect each tier's ceiling, with the excess passed on.
 *
 * Water-filling: start from the raw shares, pin every share that is over its
 * ceiling, and hand what that frees to the shares still under theirs in
 * proportion to how much they wanted. Repeat until nothing is over. Because the
 * ceilings are validated to sum to at least 1, there is always somewhere for the
 * excess to go, so the result sums to 1 and no ceiling is exceeded.
 */
function applyCeilings(shares: readonly number[], ceilings: readonly number[]): number[] {
  const result = new Array<number>(shares.length).fill(0);
  let pending = shares.map((_share, index) => index);
  let remaining = 1;
  for (let pass = 0; pass <= shares.length && pending.length > 0; pass += 1) {
    let pool = 0;
    for (const index of pending) pool += shares[index]!;
    if (!(pool > 0)) break;
    const over = pending.filter((index) => (remaining * shares[index]!) / pool > ceilings[index]!);
    if (over.length === 0) {
      for (const index of pending) result[index] = (remaining * shares[index]!) / pool;
      return result;
    }
    for (const index of over) {
      // `Number.POSITIVE_INFINITY` never lands here, so a capped value is always finite.
      const cap = ceilings[index]!;
      result[index] = cap;
      remaining -= cap;
    }
    pending = pending.filter((index) => !over.includes(index));
  }
  // Only reachable if every share was pinned, which the ceiling validation rules
  // out. Normalising keeps the promise that the chances sum to 1, and the
  // `rarityCeilingProblems` check is what makes the situation visible rather than
  // silently accepted.
  const total = result.reduce((sum, value) => sum + value, 0);
  return total > 0 ? result.map((value) => value / total) : result;
}

/**
 * Every candidate on a continent with its match score and resulting chance.
 *
 * `wildcards` is how many any-colour pixels the collection carries, the seed's own
 * included. With none of them and nothing gathered, every weight is zero and every
 * chance is zero: nothing has been earned yet. That is a displayable state, which
 * is why it is returned rather than raised; `selectEncounter` is where an actual
 * draw refuses it, because a reveal cannot be reached that way in play.
 */
export function rankCandidates(mixture: ColourMixture, continent: ContinentId, wildcards = 0): EncounterCandidate[] {
  const augmented = augmentMixture(mixture, wildcards);
  const scored = profileMixturesOn(continent).map((entry) => ({
    speciesId: entry.profile.speciesId,
    rarity: getCreature(entry.profile.speciesId).rarity,
    matchScore: matchScore(augmented, entry.mixture),
  }));
  sortCandidates(scored);

  const weights = scored.map((entry) => rarityRung(entry.rarity).weight * entry.matchScore ** COLLECT_CONFIG.sharpness);
  const totalWeight = weights.reduce((sum, value) => sum + value, 0);
  if (!(totalWeight > 0)) return scored.map((entry) => ({ ...entry, probability: 0 }));

  const shares = weights.map((weight) => weight / totalWeight);
  const capped = applyCeilings(shares, scored.map((entry) => rarityRung(entry.rarity).ceiling));
  return scored.map((entry, index) => ({ ...entry, probability: capped[index]! }));
}

/**
 * Picks one creature.
 *
 * `random` is supplied by the caller so a reveal can be reproduced exactly from
 * the collection and the reveal seed.
 *
 * An empty collection with no any-colour pixel is refused rather than resolved to
 * something arbitrary. In play it cannot happen: the seed the player launches is
 * itself an any-colour pixel, so a reveal always carries at least one. Reaching
 * this state therefore means the caller has asked for an encounter the game never
 * offers, which is reported instead of being papered over with a fallback.
 */
export function selectEncounter(
  mixture: ColourMixture,
  continent: ContinentId,
  random: () => number,
  wildcards = 0,
): EncounterSelection {
  const candidates = rankCandidates(mixture, continent, wildcards);
  if (candidates.length === 0) {
    throw new Error(`Garaimon: no creatures are defined for ${continent}, so no encounter can be chosen.`);
  }
  const total = candidates.reduce((sum, candidate) => sum + candidate.probability, 0);
  if (!(total > 0)) {
    throw new Error(
      `Garaimon: no encounter can be chosen from ${continent} because nothing has been gathered and no any-colour pixel is held. ` +
        'The seed the player launches is always one any-colour pixel, so a reveal in this state is a bug rather than an empty collection.',
    );
  }
  const draw = Math.max(0, Math.min(1 - 1e-12, random()));
  let cumulative = 0;
  for (const candidate of candidates) {
    cumulative += candidate.probability;
    if (draw < cumulative) return { chosenSpeciesId: candidate.speciesId, candidates };
  }
  // Floating-point residue only: the last candidate takes the remainder.
  return { chosenSpeciesId: candidates[candidates.length - 1]!.speciesId, candidates };
}

/** The chance a particular creature is chosen, 0 when it is not on the continent. */
export function probabilityOf(speciesId: string, candidates: readonly EncounterCandidate[]): number {
  return candidates.find((candidate) => candidate.speciesId === speciesId)?.probability ?? 0;
}

/**
 * Measures the selection by sampling it many times.
 *
 * Used by tests and by the sweep harness, so the stated behaviour of the policy
 * is checked against what it actually does rather than trusted.
 */
export function sampleProbabilities(
  mixture: ColourMixture,
  continent: ContinentId,
  trials: number,
  random: () => number,
  wildcards = 0,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (let trial = 0; trial < trials; trial += 1) {
    const { chosenSpeciesId } = selectEncounter(mixture, continent, random, wildcards);
    counts.set(chosenSpeciesId, (counts.get(chosenSpeciesId) ?? 0) + 1);
  }
  const result = new Map<string, number>();
  for (const [speciesId, count] of counts) result.set(speciesId, count / trials);
  return result;
}

/** The best match on a continent, for readouts that name what the player is closest to. */
export function bestMatch(mixture: ColourMixture, continent: ContinentId, wildcards = 0): EncounterCandidate {
  const candidates = rankCandidates(mixture, continent, wildcards);
  const first = candidates[0];
  if (!first) throw new Error(`Garaimon: no creatures are defined for ${continent}.`);
  return first;
}

export { profileFor };
